import React, { useState, useRef, useEffect } from "react";
import { track } from "../utils/analytics";
import { useNavigate } from "react-router-dom";
import LaunchBanner from "../components/LaunchBanner";
import Header from "../components/Header";
import api from "../api";

import "../styles/RequestVenue.css";

const RequestVenue = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    submitter_email: "",
    context: "",
    company: "", // honeypot
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  // Track page view once
  useEffect(() => {
    track("venue_request_page_view");
  }, []);

  // throttle change tracking
  const changeTimeouts = useRef({});
  const THROTTLE_MS = 900;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));

    if (changeTimeouts.current[name]) {
      clearTimeout(changeTimeouts.current[name]);
    }
    changeTimeouts.current[name] = setTimeout(() => {
      track("rv_field_change", { field: name, length: value.length });
    }, THROTTLE_MS);
  };

  const onFocus = (field) => track("rv_field_focus", { field });
  const onBlur = (field) =>
    track("rv_field_blur", { field, value_present: !!form[field]?.trim() });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.company) {
      // honeypot triggered
      track("rv_honeypot_triggered", { company: form.company });
      return;
    }

    setLoading(true);
    setError("");

    track("venue_request_submit_click", {
      name_present: !!form.name.trim(),
      email_present: !!form.submitter_email.trim(),
      context_present: !!form.context.trim(),
    });

    try {
      const res = await api.post("/venues/request", {
        name: form.name,
        submitter_email: form.submitter_email,
        context: form.context,
      });

      const data = res.data;

      if (data.status === "queued") {
        setSuccess(true);
        setRequestId(data.request_id);
        track("venue_request_submitted", {
          request_id: data.request_id,
          email_provided: !!form.submitter_email.trim(),
        });
      } else {
        throw new Error(data.message || "Unknown error");
      }
    } catch (err) {
      console.error(err);
      // Pull message from axios error
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong on our side. Please try again in a moment.";
      setError(msg);
      track("venue_request_submit_error", {
        message: msg,
        status: err?.response?.status,
        request_body: {
          name: form.name,
          email_present: !!form.submitter_email.trim(),
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(requestId);
      track("venue_request_id_copied", { request_id: requestId });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Copy failed. Please select and copy manually.");
      track("venue_request_id_copy_failed", { request_id: requestId });
    }
  };

  const goBackToList = () => {
    track("rv_back_to_venue_list_click", { from: "request_success" });
    navigate("/check-venue");
  };

  return (
    <div className="request-venue-root">
      <LaunchBanner
        goToSignup={() => {
          track("rv_launchbanner_signup_click");
          navigate("/signup");
        }}
      />
      <Header />

      <div className="request-venue-page section-container">
        <div className="rv-head">
          <h1 className="rv-title">Request a venue</h1>
          <p className="rv-desc">
            Didn’t find your venue? Tell us and we’ll add it—typically within 24
            hours.
          </p>
        </div>
        {!success ? (
          <form
            className="rv-form"
            onSubmit={handleSubmit}
            noValidate
            aria-busy={loading}
          >
            {error && (
              <div className="rv-error" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <div className="rv-group">
              <label htmlFor="name">
                Venue Name * <span className="sr-only">(required)</span>
              </label>
              <input
                aria-required="true"
                aria-invalid={!!error && !form.name.trim()}
                id="name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={onChange}
                onFocus={() => onFocus("name")}
                onBlur={() => onBlur("name")}
                placeholder="e.g., The Sunset Terrace at XYZ Resort"
              />
            </div>

            <div className="rv-group">
              <label htmlFor="context">
                Address / City / State / Any details
              </label>
              <textarea
                id="context"
                name="context"
                value={form.context}
                onChange={onChange}
                onFocus={() => onFocus("context")}
                onBlur={() => onBlur("context")}
                placeholder="Street address, city/state, website link, etc."
              />
            </div>

            <div className="rv-group">
              <label htmlFor="submitter_email">
                Your email (optional, for updates)
              </label>
              <input
                id="submitter_email"
                name="submitter_email"
                type="email"
                value={form.submitter_email}
                onChange={onChange}
                onFocus={() => onFocus("submitter_email")}
                onBlur={() => onBlur("submitter_email")}
                placeholder="you@example.com"
              />
            </div>

            {/* Honeypot field (hidden) */}
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={onChange}
              tabIndex={-1}
              autoComplete="off"
              style={{ position: "absolute", left: "-9999px" }}
            />

            <div className="rv-actions">
              <button
                type="submit"
                className="btn btn-primary btn-auto"
                disabled={loading || !form.name.trim()}
              >
                {loading ? (
                  <>
                    <span className="rv-spinner" aria-hidden="true" />{" "}
                    Sending...
                  </>
                ) : (
                  "Submit request"
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="rv-success" aria-live="polite" role="status">
            <h2>Thanks! 🎉</h2>
            <p>
              We got your request and will add the venue ASAP.
              {form.submitter_email
                ? " You’ll get an email when it’s live."
                : " Save this request ID to check back later."}
            </p>
            {requestId && (
              <div className="rv-id-box">
                <p>
                  <strong>Your request ID:</strong>
                </p>
                <code>{requestId}</code>
                <button
                  className="btn btn-tertiary btn-auto rv-copy-btn"
                  onClick={copyId}
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            )}
            <button
              className="btn btn-secondary btn-auto"
              onClick={goBackToList}
            >
              Back to venue list
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestVenue;
